import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { registerSchema } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
  Label,
} from '~/components/ui/primitives';
import { homeRouteFor, useAuth } from '~/lib/auth';
import { messageOf } from '~/lib/queries';

/**
 * Creation de compte.
 *
 * RG-04 : lorsque la page est atteinte a l'issue d'une reservation, l'URL porte un
 * `claim` et les coordonnees deja saisies. Le compte invite est alors CONVERTI —
 * la reservation reste rattachee, aucun doublon n'est cree.
 */
export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const guestClaimToken = searchParams.get('claim') ?? undefined;
  const reference = searchParams.get('ref') ?? undefined;

  const [form, setForm] = React.useState({
    email: searchParams.get('email') ?? '',
    firstName: searchParams.get('prenom') ?? '',
    lastName: searchParams.get('nom') ?? '',
    phone: searchParams.get('tel') ?? '',
    password: '',
    passwordConfirmation: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const update = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    const parsed = registerSchema.safeParse({
      ...form,
      phone: form.phone || undefined,
      guestClaimToken,
    });

    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join('.'), issue.message]),
        ),
      );
      return;
    }

    setSubmitting(true);
    try {
      const account = await register(parsed.data);
      toast.success('Votre compte est cree.');
      navigate(homeRouteFor(account.role), { replace: true });
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col justify-center px-4 py-12">
      {reference ? (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-success/40 bg-success/10 p-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          <div className="space-y-0.5 text-sm">
            <p className="font-medium">Rendez-vous confirme — {reference}</p>
            <p className="text-muted-foreground">
              Creez votre mot de passe pour suivre et modifier votre rendez-vous.
            </p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {guestClaimToken ? 'Finaliser votre compte' : 'Creer un compte'}
          </CardTitle>
          <CardDescription>
            {guestClaimToken
              ? 'Vos coordonnees sont deja renseignees. Il ne reste qu’a definir un mot de passe.'
              : 'Suivez vos interventions et retrouvez vos cycles enregistres.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Prenom" htmlFor="firstName" error={errors.firstName} required>
                <Input
                  value={form.firstName}
                  onChange={(event) => update('firstName')(event.target.value)}
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Nom" htmlFor="lastName" error={errors.lastName} required>
                <Input
                  value={form.lastName}
                  onChange={(event) => update('lastName')(event.target.value)}
                  autoComplete="family-name"
                />
              </Field>
            </div>

            <Field label="Adresse e-mail" htmlFor="email" error={errors.email} required>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => update('email')(event.target.value)}
                autoComplete="email"
                // L'adresse issue de la reservation ne doit pas etre modifiee :
                // elle est la cle de rattachement du compte invite.
                readOnly={!!guestClaimToken}
              />
            </Field>

            <Field label="Telephone" htmlFor="phone" error={errors.phone} hint="Facultatif">
              <Input
                type="tel"
                value={form.phone}
                onChange={(event) => update('phone')(event.target.value)}
                autoComplete="tel"
                placeholder="06 12 34 56 78"
              />
            </Field>

            <Field
              label="Mot de passe"
              htmlFor="password"
              error={errors.password}
              required
              hint="12 caracteres minimum, avec majuscule, minuscule, chiffre et caractere special."
            >
              <Input
                type="password"
                value={form.password}
                onChange={(event) => update('password')(event.target.value)}
                autoComplete="new-password"
              />
            </Field>

            <Field
              label="Confirmation"
              htmlFor="passwordConfirmation"
              error={errors.passwordConfirmation}
              required
            >
              <Input
                type="password"
                value={form.passwordConfirmation}
                onChange={(event) => update('passwordConfirmation')(event.target.value)}
                autoComplete="new-password"
              />
            </Field>

            <div className="flex items-start gap-2">
              <Checkbox
                id="acceptTerms"
                checked={form.acceptTerms}
                onCheckedChange={(checked) => update('acceptTerms')(checked === true)}
                aria-invalid={errors.acceptTerms ? true : undefined}
              />
              <Label htmlFor="acceptTerms" className="text-sm font-normal leading-snug">
                J'accepte les conditions generales et la politique de confidentialite.
              </Label>
            </div>
            {errors.acceptTerms ? (
              <p role="alert" className="-mt-2 text-xs font-medium text-destructive">
                {errors.acceptTerms}
              </p>
            ) : null}

            <Button type="submit" loading={submitting} className="w-full">
              {guestClaimToken ? 'Finaliser mon compte' : 'Creer mon compte'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Deja inscrit ?{' '}
            <Link
              to="/connexion"
              className="font-medium text-foreground underline underline-offset-4"
            >
              Se connecter
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
